import { useState, useEffect, useCallback } from 'react';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../../../../lib/supabase';
import { formatDate, formatDuration } from '../../../../utils/dateUtils';
import TimeEntryEditModal from './TimeEntryEditModal';
import { Avatar, KebabMenu } from '../../../ui';

interface TimeEntryHistoryProps {
  clientId: string;
}

interface HistoryEntry {
  id: string;
  task_name: string;
  start_time: string;
  end_time: string | null;
  notes: string | null;
  created_at: string | null;
  user_id: string;
  users?: { name: string | null; email: string | null } | null;
}

export default function TimeEntryHistory({ clientId }: TimeEntryHistoryProps) {
  const [timeEntries, setTimeEntries] = useState<HistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<HistoryEntry | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Fetch time entries for the client
  const fetchTimeEntries = useCallback(async () => {
    if (!clientId) return;

    try {
      setIsLoading(true);
      setError(null);

      // First, fetch time entries
      const { data: timeEntriesData, error: timeEntriesError } = await supabase
        .from('time_entries')
        .select(`
          id,
          task_name,
          start_time,
          end_time,
          notes,
          created_at,
          user_id
        `)
        .eq('client_id', clientId)
        .order('start_time', { ascending: false });

      if (timeEntriesError) throw timeEntriesError;

      if (!timeEntriesData || timeEntriesData.length === 0) {
        setTimeEntries([]);
        return;
      }

      // Get unique user IDs
      const userIds = [...new Set(timeEntriesData.map((entry) => entry.user_id))].filter(Boolean);

      // Fetch user names separately
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, name, email')
        .in('id', userIds);

      if (usersError) {
        console.warn('Could not fetch user names:', usersError);
      }

      // Map user data to time entries
      const timeEntriesWithUsers = timeEntriesData.map((entry) => {
        const user = usersData?.find((u) => u.id === entry.user_id);
        return {
          ...entry,
          users: user ? { name: user.name, email: user.email } : null,
        };
      });

      setTimeEntries(timeEntriesWithUsers);
    } catch (err) {
      console.error('Error fetching time entries:', err);
      setError('Failed to load time tracking history');
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchTimeEntries();
  }, [fetchTimeEntries]);

  // Delete time entry
  const handleDeleteEntry = async (entryId: string) => {
    if (!confirm('Are you sure you want to delete this time entry?')) return;

    try {
      setError(null);

      const { error } = await supabase.from('time_entries').delete().eq('id', entryId);

      if (error) throw error;

      // Refresh the list
      fetchTimeEntries();
    } catch (err) {
      console.error('Error deleting time entry:', err);
      setError('Failed to delete time entry');
    }
  };

  // Handle edit time entry
  const handleEditEntry = (entry: HistoryEntry) => {
    setEditingEntry(entry);
    setIsEditModalOpen(true);
  };

  // Handle save edited time entry
  const handleSaveEdit = async (updatedEntry: {
    id: string;
    task_name: string;
    start_time: string;
    end_time: string | null;
    notes: string;
  }) => {
    try {
      setError(null);

      const { error } = await supabase
        .from('time_entries')
        .update({
          task_name: updatedEntry.task_name,
          start_time: updatedEntry.start_time,
          end_time: updatedEntry.end_time,
          notes: updatedEntry.notes,
        })
        .eq('id', updatedEntry.id);

      if (error) throw error;

      // Close modal and refresh the list
      setIsEditModalOpen(false);
      fetchTimeEntries();
    } catch (err) {
      console.error('Error updating time entry:', err);
      setError('Failed to update time entry');
    }
  };

  // Calculate duration between start and end time
  const calculateDuration = (startTime: string, endTime: string | null) => {
    if (!endTime) return 'In Progress';

    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    const durationInSeconds = Math.floor((end - start) / 1000);

    return formatDuration(durationInSeconds);
  };

  return (
    <div>
      {error && (
        <div
          style={{
            marginBottom: '14px',
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-down-soft)',
            border: '1px solid rgba(229, 72, 77, 0.25)',
            color: 'var(--danger-400)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {error}
          <button
            onClick={() => setError(null)}
            className="hover-underline"
            style={{
              marginLeft: '8px',
              border: 'none',
              background: 'transparent',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: 'var(--text-xs)',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="t-eyebrow" style={{ marginBottom: '10px' }}>History</div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
          <div
            className="animate-spin w-6 h-6 border-2 rounded-full"
            style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
          />
        </div>
      ) : timeEntries.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
          No time entries logged for this client yet.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {timeEntries.map((entry) => (
            <div
              key={entry.id}
              className="hover-wash"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '9px 10px',
                margin: '0 -10px',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <Avatar name={entry.users?.name || entry.users?.email || '?'} size={24} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-body)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {entry.task_name}
                </span>
                <span className="t-num" style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '2px' }}>
                  {formatDate(entry.start_time, 'MMM d, h:mm a')}
                  {entry.end_time ? ` – ${formatDate(entry.end_time, 'h:mm a')}` : ' – Now'}
                </span>
                {entry.notes && (
                  <span
                    style={{
                      display: 'block',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-muted)',
                      marginTop: '3px',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {entry.notes}
                  </span>
                )}
              </span>
              <span
                className="t-num"
                style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', whiteSpace: 'nowrap' }}
              >
                {calculateDuration(entry.start_time, entry.end_time)}
              </span>
              <KebabMenu
                label="Time entry actions"
                actions={[
                  { label: 'Edit Entry', icon: faPen, onClick: () => handleEditEntry(entry) },
                  { label: 'Delete', icon: faTrash, danger: true, onClick: () => handleDeleteEntry(entry.id) },
                ]}
              />
            </div>
          ))}
        </div>
      )}

      {/* Edit Panel */}
      {isEditModalOpen && editingEntry && (
        <TimeEntryEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          timeEntry={editingEntry}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
}
